import React, { useState, useRef, useEffect } from "react";
import { ArrowLeft, Upload, ChevronDown, ChevronUp, Plus, Minus } from "lucide-react";
import { Button } from "../UI/Button";
import TextInput from "../UI/TextInput";
import type { CreateOrderRequest, OrderOption } from "../../../domain/types/orders";
import type {
  ShopProduct,
  ProductOptionResponse,
  Attribute,
  SampleValue,
  Option,
  CombinationRequest,
  AttributeIdRequest,
  CalculatePriceResponse,
} from "../../../domain/types/shop-product";

interface CompanyMember {
  id: number;
  name: string;
  email: string;
  mobile: string;
}

interface OrderCreateFormProps {
  companyId: string;
  shopProducts: ShopProduct[];
  selectedProduct: ShopProduct | null;
  productOptions: ProductOptionResponse | null;
  calculatedPrice: CalculatePriceResponse | null;
  companyMembers: CompanyMember[];
  onBack: () => void;
  onProductSelect: (productId: number) => Promise<void>;
  onCalculatePrice: (combinations: CombinationRequest[], productCount: number) => Promise<void>;
  onSubmit: (orderData: CreateOrderRequest, file: File | null, selectedMemberId: number | null) => Promise<void>;
}

export const OrderCreateForm: React.FC<OrderCreateFormProps> = ({
  companyId,
  shopProducts,
  selectedProduct,
  productOptions,
  calculatedPrice,
  companyMembers,
  onBack,
  onProductSelect,
  onCalculatePrice,
  onSubmit,
}) => {
  // 폼 상태
  const [orderName, setOrderName] = useState("");
  const [productCount, setProductCount] = useState(1);
  const [isGuestOrder, setIsGuestOrder] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [memberSearchKeyword, setMemberSearchKeyword] = useState("");
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestMobile, setGuestMobile] = useState("");
  const [initialStatus, setInitialStatus] = useState<"BEFORE_PAYMENT" | "RECEIVED">("BEFORE_PAYMENT");
  const [memo, setMemo] = useState("");
  const [orderFile, setOrderFile] = useState<File | null>(null);

  // 옵션 선택 상태
  const [selectedAttributes, setSelectedAttributes] = useState<{[optionId: number]: {[attributeId: number]: AttributeIdRequest}}>({});
  const [optionQuantities, setOptionQuantities] = useState<{[combinationId: number]: number}>({});

  // 옵션 UI 확장 상태
  const [expandedOptions, setExpandedOptions] = useState<{[optionId: number]: boolean}>({});

  // 회원 드롭다운 외부 클릭 감지
  const memberDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target as Node)) {
        setShowMemberDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 속성 선택 핸들러
  const handleAttributeSelect = (optionId: number, attributeId: number, sampleValueId: number, value: string) => {
    setSelectedAttributes(prev => ({
      ...prev,
      [optionId]: {
        ...prev[optionId],
        [attributeId]: {
          attributeId,
          sampleValueId,
          value,
        },
      },
    }));
  };

  // 텍스트 입력 속성 핸들러
  const handleTextAttributeChange = (optionId: number, attributeId: number, value: string) => {
    setSelectedAttributes(prev => ({
      ...prev,
      [optionId]: {
        ...prev[optionId],
        [attributeId]: {
          attributeId,
          value,
        },
      },
    }));
  };

  // 조합 생성 및 가격 계산
  const handleRecalculate = async () => {
    if (!selectedProduct || !productOptions) return;

    const combinations: CombinationRequest[] = [];

    // 각 옵션에 대해 조합 생성
    for (const option of productOptions.options) {
      const selectedAttrs = selectedAttributes[option.id];
      if (!selectedAttrs) continue;

      const attributeIds = Object.values(selectedAttrs);
      if (attributeIds.length === 0) continue;

      // 해당 조합 ID 찾기 (간단하게 첫 번째 조합 사용)
      const combination = productOptions.combinations.find(c =>
        c.attributeIds.some(aid => attributeIds.some(attr => attr.attributeId === aid))
      );

      if (combination) {
        combinations.push({
          combinationId: combination.id,
          attributeIds,
          selectableCount: optionQuantities[combination.id] || 1,
        });
      }
    }

    await onCalculatePrice(combinations, productCount);
  };

  // 제출 핸들러
  const handleSubmit = async () => {
    if (!selectedProduct) return;

    const orderData: CreateOrderRequest = {
      name: orderName,
      productId: selectedProduct.id,
      productCount,
      productPrice: calculatedPrice?.productPrice ? calculatedPrice.productPrice / calculatedPrice.productQuantity : selectedProduct.price,
      initialStatus,
      purchaserId: isGuestOrder ? undefined : selectedMemberId || undefined,
      guestInfo: isGuestOrder ? {
        name: guestName,
        email: guestEmail || undefined,
        mobile: guestMobile || undefined,
      } : undefined,
      memo: memo || undefined,
    };

    await onSubmit(orderData, orderFile, selectedMemberId);
  };

  // 회원 필터링: 검색어가 있으면 필터링, 없으면 최대 5명
  const filteredMembers = React.useMemo(() => {
    console.log('Total members:', companyMembers.length);
    console.log('Search keyword:', memberSearchKeyword);

    if (!memberSearchKeyword || memberSearchKeyword.trim() === '') {
      const result = companyMembers.slice(0, 5);
      console.log('Showing first 5 members:', result);
      return result;
    }

    const keyword = memberSearchKeyword.toLowerCase();
    const filtered = companyMembers.filter(m =>
      m.name.toLowerCase().includes(keyword) ||
      m.email.toLowerCase().includes(keyword)
    );
    console.log('Filtered members:', filtered);
    return filtered;
  }, [companyMembers, memberSearchKeyword]);

  return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="w-full">
        {/* 헤더 */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">주문 생성</h1>
        </div>

        <div className="space-y-6 text-left">
          {/* 주문 정보 섹션 */}
          <div className="space-y-6">
            {/* 기본 정보 카드 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 text-left">기본 정보</h2>

              {/* 주문명 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  주문명 <span className="text-red-500">*</span>
                </label>
                <TextInput
                  value={orderName}
                  onChange={(e) => setOrderName(e.target.value)}
                  placeholder="주문명을 입력하세요"
                />
              </div>

              {/* 상품 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  상품 선택 <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  value={selectedProduct?.id || 0}
                  onChange={(e) => onProductSelect(Number(e.target.value))}
                >
                  <option value={0}>상품을 선택하세요</option>
                  {shopProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} - ₩{product.price.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* 상품 옵션 카드 */}
            {selectedProduct && productOptions && productOptions.options.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 text-left">상품 옵션</h2>
                <div className="space-y-4">
                  {productOptions.options.map((option) => (
                    <div key={option.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* 옵션 헤더 */}
                      <div
                        className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition"
                        onClick={() => setExpandedOptions(prev => ({...prev, [option.id]: !prev[option.id]}))}
                      >
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">
                            {option.name}
                            {option.isRequiredOption && <span className="text-red-500 ml-1">*</span>}
                          </h3>
                          {option.description && (
                            <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {option.quantityApplyType === "PER_ITEM"
                              ? "(상품 개수당 적용)"
                              : option.quantityApplyType === "PER_ORDER"
                              ? "(주문당 1회 적용)"
                              : option.quantityApplyType === "SAME_AS_PRODUCT_QUANTITY"
                              ? "(상품 수량과 동일)"
                              : "(선택 가능)"}
                          </p>
                        </div>
                        {expandedOptions[option.id] ? (
                          <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </div>

                      {/* 옵션 본문 */}
                      {expandedOptions[option.id] && (
                        <div className="p-4 space-y-4">
                          {option.attributes.map((attribute) => (
                            <div key={attribute.id}>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {attribute.name}
                                {attribute.isRequiredAttribute && <span className="text-red-500 ml-1">*</span>}
                              </label>

                              {/* SELECT/ENUM/SIZE/GRAMMAGE 타입 */}
                              {(attribute.attributeType === "SELECT" || attribute.attributeType === "ENUM" || attribute.attributeType === "SIZE" || attribute.attributeType === "GRAMMAGE") && attribute.sampleValues && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                  {attribute.sampleValues.map((sample: SampleValue) => {
                                    const isSelected = selectedAttributes[option.id]?.[attribute.id]?.sampleValueId === sample.id;
                                    return (
                                      <button
                                        key={sample.id}
                                        type="button"
                                        className={`p-3 border-2 rounded-lg text-sm transition ${
                                          isSelected
                                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                                            : 'border-gray-300 bg-white hover:border-gray-400'
                                        }`}
                                        onClick={() => handleAttributeSelect(option.id, attribute.id, sample.id, sample.value)}
                                      >
                                        <div className="font-medium">{sample.value}</div>
                                        {sample.description && (
                                          <div className="text-xs text-gray-500 mt-1">{sample.description}</div>
                                        )}
                                        {sample.width && sample.height && (
                                          <div className="text-xs text-gray-400 mt-1">
                                            {sample.width} x {sample.height}mm
                                          </div>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}

                              {/* TEXT/NUMBER 타입 */}
                              {(attribute.attributeType === "TEXT" || attribute.attributeType === "NUMBER") && (
                                <TextInput
                                  type={attribute.attributeType === "NUMBER" ? "number" : "text"}
                                  value={selectedAttributes[option.id]?.[attribute.id]?.value || ""}
                                  onChange={(e) => handleTextAttributeChange(option.id, attribute.id, e.target.value)}
                                  placeholder={`${attribute.name}을(를) 입력하세요`}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 주문자 정보 카드 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 text-left">주문자 정보</h2>

              {/* 회원/비회원 선택 */}
              <div className="mb-4">
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={!isGuestOrder}
                      onChange={() => {
                        setIsGuestOrder(false);
                        setGuestName("");
                        setGuestEmail("");
                        setGuestMobile("");
                      }}
                      className="mr-2 w-4 h-4"
                    />
                    <span className="text-sm font-medium">회원 주문</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={isGuestOrder}
                      onChange={() => {
                        setIsGuestOrder(true);
                        setSelectedMemberId(null);
                        setMemberSearchKeyword("");
                      }}
                      className="mr-2 w-4 h-4"
                    />
                    <span className="text-sm font-medium">비회원 주문</span>
                  </label>
                </div>
              </div>

              {/* 회원 선택 */}
              {!isGuestOrder && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    회원 선택 <span className="text-red-500">*</span>
                  </label>

                  {/* 선택된 회원 표시 */}
                  {selectedMemberId && companyMembers.find(m => m.id === selectedMemberId) && (
                    <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">
                            {companyMembers.find(m => m.id === selectedMemberId)?.name}
                          </div>
                          <div className="text-sm text-gray-600">
                            {companyMembers.find(m => m.id === selectedMemberId)?.email}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedMemberId(null);
                            setMemberSearchKeyword("");
                          }}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          변경
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 검색 입력 (선택되지 않았을 때만 표시) */}
                  {!selectedMemberId && (
                    <div className="relative" ref={memberDropdownRef}>
                      <TextInput
                        value={memberSearchKeyword}
                        onChange={(e) => {
                          setMemberSearchKeyword(e.target.value);
                          if (!showMemberDropdown) {
                            setShowMemberDropdown(true);
                          }
                        }}
                        onFocus={() => setShowMemberDropdown(true)}
                        placeholder="회원 이름 또는 이메일로 검색 (또는 클릭하여 목록 보기)"
                      />
                  {showMemberDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredMembers.length > 0 ? (
                        filteredMembers.map(member => (
                          <div
                            key={member.id}
                            className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition ${
                              selectedMemberId === member.id ? 'bg-blue-50' : ''
                            }`}
                            onClick={() => {
                              setSelectedMemberId(member.id);
                              // 검색어를 회원 정보로 변경하면 필터링이 다시 작동함
                              // 대신 별도 표시 영역을 만들거나, 검색어를 초기화
                              setMemberSearchKeyword("");
                              setShowMemberDropdown(false);
                            }}
                          >
                            <div className="font-medium text-gray-900">{member.name}</div>
                            <div className="text-sm text-gray-500">{member.email}</div>
                            {member.mobile && (
                              <div className="text-xs text-gray-400">{member.mobile}</div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                          {memberSearchKeyword ? '검색 결과가 없습니다.' : '등록된 회원이 없습니다.'}
                        </div>
                      )}
                    </div>
                  )}
                    </div>
                  )}
                </div>
              )}

              {/* 비회원 정보 */}
              {isGuestOrder && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      이름 <span className="text-red-500">*</span>
                    </label>
                    <TextInput
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="이름을 입력하세요"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      이메일
                    </label>
                    <TextInput
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="이메일을 입력하세요"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      전화번호
                    </label>
                    <TextInput
                      value={guestMobile}
                      onChange={(e) => setGuestMobile(e.target.value)}
                      placeholder="전화번호를 입력하세요"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* 추가 정보 카드 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 text-left">추가 정보</h2>

              {/* 결제 상태 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  결제 상태
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  value={initialStatus}
                  onChange={(e) => setInitialStatus(e.target.value as "BEFORE_PAYMENT" | "RECEIVED")}
                >
                  <option value="BEFORE_PAYMENT">결제 전</option>
                  <option value="RECEIVED">결제 완료</option>
                </select>
              </div>

              {/* 메모 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  메모
                </label>
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="메모를 입력하세요"
                />
              </div>

              {/* 파일 업로드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  주문 원본 파일
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50 hover:bg-gray-100 transition">
                  <input
                    type="file"
                    id="order-file"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setOrderFile(e.target.files[0]);
                      }
                    }}
                  />
                  <label htmlFor="order-file" className="cursor-pointer">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    {orderFile ? (
                      <p className="text-sm text-gray-700 font-medium">{orderFile.name}</p>
                    ) : (
                      <p className="text-sm text-gray-500">파일을 선택하거나 드래그하세요</p>
                    )}
                  </label>
                </div>
              </div>
            </div>

            {/* 수량 및 가격 정보 카드 */}
            {selectedProduct && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 text-left">수량 및 가격 정보</h2>

                {/* 선택된 상품명 */}
                <div className="mb-4 pb-4 border-b">
                  <label className="block text-sm font-medium text-gray-700 mb-1">선택된 상품</label>
                  <h3 className="font-medium text-gray-900">{selectedProduct.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    기본 가격: ₩{selectedProduct.price.toLocaleString()}
                  </p>
                </div>

                {/* 수량 선택 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    수량 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center border border-gray-300 rounded-lg w-40">
                    <button
                      type="button"
                      className="p-3 hover:bg-gray-100 transition"
                      onClick={() => setProductCount(Math.max(1, productCount - 1))}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={productCount}
                      onChange={(e) => setProductCount(Math.max(1, Number(e.target.value)))}
                      className="flex-1 text-center border-0 focus:outline-none font-medium"
                    />
                    <button
                      type="button"
                      className="p-3 hover:bg-gray-100 transition"
                      onClick={() => setProductCount(productCount + 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* 가격 재계산 버튼 */}
                {productOptions && productOptions.options.length > 0 && (
                  <div className="mb-4">
                    <Button
                      variant="outline"
                      onClick={handleRecalculate}
                      className="w-full sm:w-auto"
                    >
                      가격 재계산
                    </Button>
                  </div>
                )}

                {/* 계산된 가격 */}
                {calculatedPrice && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">상품 가격</span>
                        <span className="font-medium">₩{calculatedPrice.productPrice.toLocaleString()}</span>
                      </div>
                      {calculatedPrice.optionPrices.map((opt, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span className="text-gray-700">{opt.optionName}</span>
                          <span>₩{opt.optionPrice.toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="flex justify-between border-t border-blue-300 pt-2">
                        <span className="text-gray-700">부가세</span>
                        <span>₩{calculatedPrice.vatAmount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between font-bold text-base border-t border-blue-300 pt-2">
                        <span>최종 금액</span>
                        <span className="text-blue-600">₩{calculatedPrice.totalAmount.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 제출 버튼 */}
            <div className="flex gap-3">
              <Button
                onClick={handleSubmit}
                disabled={!orderName || !selectedProduct || productCount < 1 || (!isGuestOrder && !selectedMemberId) || (isGuestOrder && !guestName)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3"
              >
                주문 생성
              </Button>
              <Button
                variant="outline"
                onClick={onBack}
                className="px-8"
              >
                취소
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
